import { type ChangeEvent, useCallback, useState } from 'react';
import {
  extractFieldErrors,
  getErrorDetails,
  requiredFieldMessage,
  requiredSubmitMessage,
} from './validation';

export type FormFieldValue = string | number | boolean | null | undefined | string[] | number[];

export type DialogFormState<T extends Record<string, FormFieldValue>> = {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  submitError: string | null;
  setField: <K extends keyof T>(key: K, value: T[K]) => void;
  handleChange: <K extends keyof T>(
    key: K,
    transform?: (value: string) => T[K],
  ) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  setSubmitError: (message: string | null) => void;
  clearFieldError: (key: keyof T) => void;
  setFieldError: (key: keyof T, message: string) => void;
  setFieldErrors: (nextErrors: Partial<Record<keyof T, string>>) => void;
  applyApiErrors: (err: unknown, fieldMap?: Partial<Record<string, keyof T>>) => boolean;
  reset: () => void;
  validateRequired: (...keys: Array<keyof T>) => boolean;
  validatePattern: (key: keyof T, pattern: RegExp, message: string) => boolean;
  validateMaxLength: (key: keyof T, max: number, message?: string) => boolean;
};

export const useDialogForm = <T extends Record<string, FormFieldValue>>(
  initial: T,
): DialogFormState<T> => {
  const [values, setValues] = useState<T>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const setField = useCallback(<K extends keyof T>(key: K, value: T[K]): void => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const { [key]: _removed, ...rest } = prev;
      return rest as Partial<Record<keyof T, string>>;
    });
    setSubmitError(null);
  }, []);

  const handleChange = useCallback(
    <K extends keyof T>(
      key: K,
      transform?: (value: string) => T[K],
    ): ((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void) =>
      (event) => {
        const raw = event.target.value;
        const next = transform ? transform(raw) : (raw as T[K]);
        setField(key, next);
      },
    [setField],
  );

  const clearFieldError = useCallback((key: keyof T): void => {
    setErrors((prev) => {
      const { [key]: _removed, ...rest } = prev;
      return rest as Partial<Record<keyof T, string>>;
    });
  }, []);

  const setFieldError = useCallback((key: keyof T, message: string): void => {
    setErrors((prev) => ({ ...prev, [key]: message }));
    setSubmitError(null);
  }, []);

  const setFieldErrors = useCallback((nextErrors: Partial<Record<keyof T, string>>): void => {
    setErrors((prev) => ({ ...prev, ...nextErrors }));
    setSubmitError(null);
  }, []);

  const applyApiErrors = useCallback(
    (err: unknown, fieldMap?: Partial<Record<string, keyof T>>): boolean => {
      const details = getErrorDetails(err);
      const fieldErrors = extractFieldErrors(details);
      if (!fieldErrors) return false;
      const nextErrors: Partial<Record<keyof T, string>> = {};
      Object.entries(fieldErrors).forEach(([field, message]) => {
        const mappedKey = fieldMap?.[field] ?? (field as keyof T);
        if (Object.prototype.hasOwnProperty.call(values, mappedKey)) {
          nextErrors[mappedKey] = message;
        }
      });
      if (Object.keys(nextErrors).length === 0) return false;
      setErrors(nextErrors);
      setSubmitError(null);
      return true;
    },
    [values],
  );

  const validateRequired = useCallback(
    (...keys: Array<keyof T>): boolean => {
      const nextErrors: Partial<Record<keyof T, string>> = {};
      keys.forEach((key) => {
        const raw = values[key];
        if (typeof raw === 'string' && raw.trim().length === 0) {
          nextErrors[key] = requiredFieldMessage;
        }
      });
      const isValid = Object.keys(nextErrors).length === 0;
      setErrors(nextErrors);
      setSubmitError(isValid ? null : requiredSubmitMessage);
      return isValid;
    },
    [values],
  );

  const validatePattern = useCallback(
    (key: keyof T, pattern: RegExp, message: string): boolean => {
      const raw = values[key];
      if (typeof raw !== 'string') return true;
      const isValid = pattern.test(raw.trim());
      setErrors((prev) => ({ ...prev, ...(isValid ? {} : { [key]: message }) }));
      return isValid;
    },
    [values],
  );

  const validateMaxLength = useCallback(
    (key: keyof T, max: number, message?: string): boolean => {
      const raw = values[key];
      if (typeof raw !== 'string') return true;
      const trimmed = raw.trim();
      const isValid = trimmed.length <= max;
      setErrors((prev) => ({
        ...prev,
        ...(isValid ? {} : { [key]: message ?? `Must be ${max} characters or less` }),
      }));
      return isValid;
    },
    [values],
  );

  const reset = useCallback(() => {
    setValues(initial);
    setErrors({});
    setSubmitError(null);
  }, [initial]);

  return {
    values,
    errors,
    submitError,
    setField,
    handleChange,
    setSubmitError,
    clearFieldError,
    setFieldError,
    setFieldErrors,
    applyApiErrors,
    reset,
    validateRequired,
    validatePattern,
    validateMaxLength,
  };
};
